import { ArchiveConflictDialog } from "@/components/studio/archive-conflict-dialog";
import { PaginationControls } from "@/components/pagination-controls";
import { LifecycleActionsMenu } from "@/components/studio/lifecycle-actions-menu";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useContentLifecycle } from "@/hooks/use-content-lifecycle";
import { ContentWorkbookService } from "@/lib/api/workbook.service";
import type { StudioContentStatus } from "@/lib/schema";
import {
  STATUS_CONFIG,
  SUPPORTED_LANGUAGES,
  formatDate,
} from "@/lib/studio-constants";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  BookOpen,
  Globe,
  Loader2,
  Plus,
  Search,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";

const PAGE_SIZE = 5;

interface WorkbookListItem {
  id: string;
  status: StudioContentStatus;
  version: number;
  baseLanguage: string;
  createdBy: string | null;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
  primaryTitle: string;
  blockCount: number;
  languageCodes: string[];
}

export default function WorkbookListPage() {
  const [, setLocation] = useLocation();
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [languageFilter, setLanguageFilter] = useState<string>("all");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearchQuery(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const statusParam =
    statusFilter !== "all" ? (statusFilter as StudioContentStatus) : undefined;
  const languageParam = languageFilter !== "all" ? languageFilter : undefined;
  const searchParam = searchQuery.trim() || undefined;

  const { data: workbookResponse, isLoading } = useQuery<{
    items: WorkbookListItem[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      totalPages: number;
    };
  }>({
    queryKey: [
      "/content-workbooks",
      {
        page,
        limit: PAGE_SIZE,
        search: searchParam,
        status: statusParam,
        language: languageParam,
      },
    ],
    queryFn: () =>
      ContentWorkbookService.findAll({
        page,
        limit: PAGE_SIZE,
        search: searchParam,
        status: statusParam,
        language: languageParam,
      }) as unknown as Promise<{
        items: WorkbookListItem[];
        pagination: {
          total: number;
          page: number;
          limit: number;
          totalPages: number;
        };
      }>,
    placeholderData: keepPreviousData,
  });

  const workbookItems = workbookResponse?.items ?? [];
  const totalItems = workbookResponse?.pagination.total ?? 0;
  const totalPages = workbookResponse?.pagination.totalPages ?? 1;
  const startIndex = totalItems === 0 ? 0 : (page - 1) * PAGE_SIZE;
  const endIndex = Math.min(startIndex + workbookItems.length, totalItems);

  const {
    submitMutation,
    approveMutation,
    rejectMutation,
    publishMutation,
    archiveMutation,
    archiveConflict,
    confirmArchive,
    dismissArchiveConflict,
    withdrawMutation,
  } = useContentLifecycle({
    contentType: "workbook",
    apiPath: "/content-workbooks",
    listRoute: "/studio/workbooks",
    editRoute: (id) => `/studio/workbooks/${id}/edit`,
  });

  return (
    <div
      className="p-6 space-y-6 max-w-6xl mx-auto"
      data-testid="workbook-list-page"
    >
      <div className="flex items-center gap-4 flex-wrap">
        <Link href="/studio">
          <Button variant="ghost" size="icon" data-testid="button-back-studio">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div className="flex-1 min-w-0">
          <h1
            className="font-serif text-2xl font-semibold text-foreground"
            data-testid="text-workbook-title"
          >
            Workbooks
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Create and manage workbook content
          </p>
        </div>
        <Link href="/studio/workbooks/new">
          <Button data-testid="button-create-workbook">
            <Plus className="h-4 w-4 mr-2" />
            Create Workbook
          </Button>
        </Link>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search workbooks..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9"
                data-testid="input-search-workbook"
              />
            </div>
            <Select
              value={statusFilter}
              onValueChange={(v) => {
                setStatusFilter(v);
                setPage(1);
              }}
            >
              <SelectTrigger
                className="w-[160px]"
                data-testid="select-status-filter"
              >
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="changes_requested">
                  Changes Requested
                </SelectItem>
                <SelectItem value="published">Published</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={languageFilter}
              onValueChange={(v) => {
                setLanguageFilter(v);
                setPage(1);
              }}
            >
              <SelectTrigger
                className="w-[160px]"
                data-testid="select-language-filter"
              >
                <SelectValue placeholder="Language" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Languages</SelectItem>
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code}>
                    {lang.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : workbookItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="p-4 rounded-full bg-muted mb-4">
                <BookOpen className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-1">
                No workbook content yet
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                Create your first workbook to get started
              </p>
              <Link href="/studio/workbooks/new">
                <Button data-testid="button-create-first-workbook">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Workbook
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[300px]">Title</TableHead>
                    <TableHead className="text-center">Blocks</TableHead>
                    <TableHead className="text-center">Languages</TableHead>
                    <TableHead>Version</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last Updated</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {workbookItems.map((item) => {
                    const statusConfig = STATUS_CONFIG[item.status];
                    return (
                      <TableRow
                        key={item.id}
                        className="cursor-pointer"
                        onClick={() =>
                          setLocation(`/studio/workbooks/${item.id}/edit`)
                        }
                        data-testid={`row-workbook-${item.id}`}
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-md bg-muted shrink-0">
                              <BookOpen className="h-4 w-4 text-muted-foreground" />
                            </div>
                            <div className="min-w-0">
                              <p
                                className="font-medium text-foreground truncate"
                                data-testid={`text-workbook-title-${item.id}`}
                              >
                                {item.primaryTitle}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <span
                            className="text-sm text-muted-foreground"
                            data-testid={`text-workbook-blocks-${item.id}`}
                          >
                            {item.blockCount}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Globe className="h-3.5 w-3.5 text-muted-foreground" />
                            <span
                              className="text-sm text-muted-foreground"
                              data-testid={`text-workbook-languages-${item.id}`}
                            >
                              {(item.languageCodes || []).length}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span
                            className="text-sm text-muted-foreground"
                            data-testid={`text-workbook-version-${item.id}`}
                          >
                            v{item.version}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={statusConfig?.variant ?? "secondary"}
                            className="no-default-hover-elevate no-default-active-elevate"
                            data-testid={`badge-status-${item.id}`}
                          >
                            {statusConfig?.label ?? item.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <span
                            className="text-sm text-muted-foreground"
                            data-testid={`text-workbook-updated-${item.id}`}
                          >
                            {formatDate(item.updatedAt)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <LifecycleActionsMenu
                            itemId={item.id}
                            status={item.status}
                            createdBy={item.createdBy}
                            onEdit={(id) =>
                              setLocation(`/studio/workbooks/${id}/edit`)
                            }
                            onSubmit={(id) => submitMutation.mutate(id)}
                            onWithdraw={(id) => withdrawMutation.mutate(id)}
                            onApprove={(id) => approveMutation.mutate(id)}
                            onReject={(id) => {
                              const note = window.prompt(
                                "Add a brief note for the coach about what to fix:",
                              );
                              if (note && note.trim())
                                rejectMutation.mutate({
                                  targetId: id,
                                  reviewNote: note.trim(),
                                });
                            }}
                            onPublish={(id) => publishMutation.mutate(id)}
                            onArchive={(id) => archiveMutation.mutate(id)}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
              <PaginationControls
                currentPage={page}
                totalPages={totalPages}
                totalItems={totalItems}
                startIndex={startIndex}
                endIndex={endIndex}
                canGoNext={page < totalPages}
                canGoPrev={page > 1}
                onPageChange={setPage}
                onNext={() => setPage((p) => Math.min(p + 1, totalPages))}
                onPrev={() => setPage((p) => Math.max(p - 1, 1))}
              />
            </>
          )}
        </CardContent>
      </Card>
      <ArchiveConflictDialog
        conflict={archiveConflict}
        onConfirm={confirmArchive}
        onCancel={dismissArchiveConflict}
      />
    </div>
  );
}
